import React, { type ReactNode } from 'react';

import { type AdminAgentArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/frame';

export const UNTRUSTED_START_MARKER = '<untrusted-data>';
export const UNTRUSTED_END_MARKER = '</untrusted-data>';
export const MAX_JSON_RENDER_DEPTH = 8;
export const MAX_JSON_RENDER_CHILDREN = 50;
export const MAX_TRACE_TIMELINE_ENTRIES = 120;

export type AdminArtifactRef = {
    readonly artifact_id: string;
    readonly artifact_type: string;
    readonly artifact_version?: number;
    readonly title: string;
    readonly summary?: string | null;
    readonly lifecycle_status?: string;
};

export type AdminArtifactDisplayMode = 'full' | 'inline';

export type AdminArtifactRendererProps = {
    readonly artifact: AdminAgentArtifact;
    readonly displayMode?: AdminArtifactDisplayMode;
    readonly onOpenArtifact?: () => void;
};

export type AdminArtifactRendererComponent = (
    props: AdminArtifactRendererProps,
) => ReactNode;

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function toRecord(value: unknown): Record<string, unknown> {
    return isRecord(value) ? value : {};
}

export function scalarString(value: unknown): string | null {
    if (typeof value === 'string' && value.trim()) {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return null;
}

export function firstString(
    value: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): string | null {
    for (const key of keys) {
        const text = scalarString(value[key]);
        if (text) {
            return text;
        }
    }
    return null;
}

export function firstArray(
    value: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): unknown {
    for (const key of keys) {
        if (Array.isArray(value[key])) {
            return value[key];
        }
    }
    return undefined;
}

export function stringValue(value: unknown): string | null {
    if (typeof value === 'string' && value.trim()) {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return null;
}

export function numberValue(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

export function booleanValue(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') {
            return true;
        }
        if (normalized === 'false') {
            return false;
        }
    }
    return null;
}

export function arrayStrings(value: unknown): ReadonlyArray<string> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((item) => {
        const text = scalarString(item);
        return text ? [text] : [];
    });
}

export function stringsFromUnknown(value: unknown): ReadonlyArray<string> {
    if (typeof value === 'string' && value.trim()) {
        return [value];
    }
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((item) => {
        const text = scalarString(item);
        return text ? [text] : [];
    });
}

export function citationsFromRecord(
    value: Record<string, unknown>,
): ReadonlyArray<string> {
    const citations = [
        ...stringsFromUnknown(value['citations']),
        ...stringsFromUnknown(value['citation']),
        ...stringsFromUnknown(value['source_citation']),
    ];
    return [...new Set(citations)];
}

export function stripUntrustedMarkers(value: string): string {
    return value
        .split(UNTRUSTED_START_MARKER)
        .join('')
        .split(UNTRUSTED_END_MARKER)
        .join('');
}

export function renderUntrustedString(value: string): ReactNode {
    const nodes: Array<ReactNode> = [];
    let cursor = 0;
    let markerIndex = 0;

    while (cursor < value.length) {
        const start = value.indexOf(UNTRUSTED_START_MARKER, cursor);
        if (start < 0) {
            nodes.push(value.slice(cursor));
            break;
        }

        const untrustedStart = start + UNTRUSTED_START_MARKER.length;
        const end = value.indexOf(UNTRUSTED_END_MARKER, untrustedStart);
        if (end < 0) {
            nodes.push(value.slice(cursor));
            break;
        }

        if (start > cursor) {
            nodes.push(value.slice(cursor, start));
        }

        nodes.push(
            <mark
                key={`untrusted-${markerIndex}`}
                className="rounded-[3px] px-1 py-0.5"
                style={{
                    // Hex fallbacks mirror the --aa-warning* tokens in
                    // theme.css: the tokens are only defined on
                    // .admin-agent-page, but this helper is also reached
                    // from /admin/agent-traces via createAdminToolArtifactRenderer,
                    // where the scope is absent and an unfallbacked var()
                    // would resolve to the inherited text color.
                    background: 'var(--aa-warning-soft, #fbf3df)',
                    color: 'var(--aa-warning, #a06a0a)',
                }}
            >
                {value.slice(untrustedStart, end)}
            </mark>,
        );
        markerIndex += 1;
        cursor = end + UNTRUSTED_END_MARKER.length;
    }

    if (nodes.length === 0) {
        return value;
    }

    return <React.Fragment>{nodes}</React.Fragment>;
}

export function isSensitiveKey(key: string): boolean {
    const normalized = key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
    const tokens = normalized.split(/[^a-z0-9]+/).filter(Boolean);
    const compact = tokens.join('');
    return (
        tokens.some((token) =>
            [
                'auth',
                'authorization',
                'credential',
                'credentials',
                'key',
                'password',
                'secret',
                'token',
            ].includes(token),
        ) || compact.includes('apikey')
    );
}

export function JsonValue({
    value,
    depth = 0,
}: {
    readonly value: unknown;
    readonly depth?: number;
}) {
    if (typeof value === 'string') {
        return (
            <span className="break-words">
                “{renderUntrustedString(value)}“
            </span>
        );
    }
    if (
        value === null ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        return <span>{JSON.stringify(value)}</span>;
    }
    if (depth >= MAX_JSON_RENDER_DEPTH) {
        return (
            <span className="text-muted-foreground">
                [truncated: maximum depth]
            </span>
        );
    }
    if (Array.isArray(value)) {
        const visibleItems = value.slice(0, MAX_JSON_RENDER_CHILDREN);
        const omittedCount = value.length - visibleItems.length;
        return (
            <div className="space-y-1 pl-3 text-xs">
                <span>[</span>
                {visibleItems.map((item, index) => (
                    <div key={index} className="pl-3">
                        <JsonValue value={item} depth={depth + 1} />
                        {index < value.length - 1 ? ',' : ''}
                    </div>
                ))}
                {omittedCount > 0 ? (
                    <div className="text-muted-foreground pl-3">
                        … {omittedCount} more{' '}
                        {omittedCount === 1 ? 'item' : 'items'}
                    </div>
                ) : null}
                <span>]</span>
            </div>
        );
    }
    if (isRecord(value)) {
        const entries = Object.entries(value);
        const visibleEntries = entries.slice(0, MAX_JSON_RENDER_CHILDREN);
        const omittedCount = entries.length - visibleEntries.length;
        return (
            <div className="space-y-1 pl-3 text-xs">
                <span>{'{'}</span>
                {visibleEntries.map(([key, child], index) => (
                    <div key={key} className="pl-3">
                        <span className="text-muted-foreground">“{key}“: </span>
                        {isSensitiveKey(key) ? (
                            <span>[redacted]</span>
                        ) : (
                            <JsonValue value={child} depth={depth + 1} />
                        )}
                        {index < entries.length - 1 ? ',' : ''}
                    </div>
                ))}
                {omittedCount > 0 ? (
                    <div className="text-muted-foreground pl-3">
                        … {omittedCount} more{' '}
                        {omittedCount === 1 ? 'field' : 'fields'}
                    </div>
                ) : null}
                <span>{'}'}</span>
            </div>
        );
    }
    return <span>{String(value)}</span>;
}

export function formatRecordInline(value: Record<string, unknown>): string {
    const entries = Object.entries(value);
    if (entries.length === 0) {
        return 'None';
    }
    return entries
        .map(([key, rawValue]) => `${key}: ${formatFilterValue(key, rawValue)}`)
        .join(', ');
}

export function formatFilterValue(key: string, value: unknown): string {
    if (isSensitiveKey(key)) {
        return '[redacted]';
    }
    if (typeof value === 'string') {
        // Never truncate marker-wrapped values: cutting a marker pair in
        // half would render the literal marker text instead of the
        // highlighted span.
        if (value.includes(UNTRUSTED_START_MARKER)) {
            return value;
        }
        return value.length > 80 ? `${value.slice(0, 80)}…` : value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (value === null || value === undefined) {
        return 'None';
    }
    return '[structured]';
}

export function formatStatusLabel(value: string | null): string {
    if (!value) {
        return 'Unknown';
    }
    const label = value.replace(/[_;]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!label) {
        return 'Unknown';
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
}

export function artifactRegistryKey(artifact: AdminAgentArtifact): string {
    return `${artifact.artifact_type}@${artifact.artifact_version}`;
}

export function formatTokenMetric(value: number | null): string {
    if (value === null) {
        return 'Unknown';
    }
    return value.toLocaleString(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
}

export function formatCountMetric(value: number | null): string {
    return value === null ? 'Unknown' : formatTokenMetric(value);
}

export const MAX_ARTIFACT_TEXT_RENDER_CHARS = 20_000;
export const MAX_ARTIFACT_RAW_RENDER_CHARS = 262_144;
const ARTIFACT_RAW_PREVIEW_CHARS = 4_096;

export function truncateArtifactText(text: string): {
    readonly text: string;
    readonly omittedChars: number;
} {
    if (text.length <= MAX_ARTIFACT_TEXT_RENDER_CHARS) {
        return { text, omittedChars: 0 };
    }
    return {
        text: text.slice(0, MAX_ARTIFACT_TEXT_RENDER_CHARS),
        omittedChars: text.length - MAX_ARTIFACT_TEXT_RENDER_CHARS,
    };
}

function downloadPayloadText(serialized: string, filename: string): void {
    const blob = new Blob([serialized], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}

/**
 * Renders an artifact payload as a JSON tree, but never unboundedly:
 * oversized payloads fall back to a short raw preview plus a download
 * action instead of dumping megabytes into the DOM.
 */
export function BoundedArtifactPayload({
    payload,
    downloadFilename = 'artifact-payload.json',
}: {
    readonly payload: unknown;
    readonly downloadFilename?: string;
}) {
    let serialized = '';
    try {
        serialized = JSON.stringify(payload) ?? '';
    } catch {
        serialized = '';
    }
    if (serialized.length <= MAX_ARTIFACT_RAW_RENDER_CHARS) {
        return <JsonValue value={payload} />;
    }
    return (
        <div className="space-y-2">
            <p className="text-muted-foreground text-xs">
                Payload is {Math.round(serialized.length / 1_024)} KB — too
                large to render inline. Preview of the first{' '}
                {ARTIFACT_RAW_PREVIEW_CHARS.toLocaleString()} characters:
            </p>
            <pre className="bg-muted/40 rounded-md px-3 py-2 font-mono text-[11px] break-all whitespace-pre-wrap">
                {serialized.slice(0, ARTIFACT_RAW_PREVIEW_CHARS)}…
            </pre>
            <button
                type="button"
                className="text-foreground cursor-pointer text-xs font-medium underline underline-offset-2"
                onClick={() =>
                    downloadPayloadText(serialized, downloadFilename)
                }
            >
                Download full payload
            </button>
        </div>
    );
}
